import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, AlertCircle, Sparkles, Send, Plus, Trash2, ChevronDown, ChevronRight, Copy, Loader2, Download } from 'lucide-react';
import { Modal, ModalMainContent } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ModalInput } from '../ui/ModalInput';
import { PlaceholderEditor } from '../ui/PlaceholderEditor';
import { Checkbox } from '../ui/Checkbox';
import Badge from '../ui/Badge';
import { useToast } from '../ui/Toaster';
import { apiService } from '../../services/api';

export const leaseQueryKey = (leaseId: string) => ['lease', leaseId] as const;

// Styled layout
const BodyTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
  .body-top-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  @media (max-width: 640px) {
    .body-top-right {
      display: none;
    }
    gap: 6px;
    margin-bottom: 4px;
  }
  @media (max-width: 767px) {
    gap: 8px;
    margin-bottom: 6px;
  }
`;

const DescriptionText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  margin: 0;
  flex: 1;
  min-width: 0;
  @media (max-width: 767px) {
    font-size: 13px;
    line-height: 1.4;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 767px) {
    gap: 6px;
  }
`;

const LabelRow = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
  display: block;
  margin-bottom: 8px;
  @media (max-width: 767px) {
    margin-bottom: 4px;
    font-size: 13px;
  }
`;

const SectionHeading = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  @media (max-width: 767px) {
    margin: 0 0 4px 0;
    font-size: 12px;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.foreground};
    box-shadow: 0 0 0 2px ${props => props.theme.muted}40;
  }
  &::placeholder {
    color: ${props => props.theme.mutedForeground};
  }
  @media (max-width: 767px) {
    min-height: 80px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 13px;
  }
`;

/* General conditions: match ui-sandbox lease-editor-modal (paste area, mono, no resize) */
const GeneralConditionsLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
  @media (max-width: 767px) {
    margin-bottom: 4px;
    gap: 6px;
  }
`;

const GeneralConditionsInput = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  background: transparent;
  color: ${props => props.theme.foreground};
  font-size: 13px;
  font-family: ui-monospace, 'SF Mono', Monaco, 'Cascadia Mono', 'Segoe UI Mono', monospace;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  resize: none;
  transition: border-color 0.2s ease;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 767px) {
    min-height: 180px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
  }
  @media (min-width: 641px) {
    padding: 16px;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.foreground};
    box-shadow: 0 0 0 2px ${props => props.theme.muted}40;
  }
  &::placeholder {
    color: ${props => props.theme.mutedForeground};
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 3px;
  }
`;

const KeyTermsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const KeyTermRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  .term-label {
    min-width: 140px;
    font-size: 13px;
    color: ${props => props.theme.mutedForeground};
  }
  .term-input {
    flex: 1;
  }
`;

const SectionCard = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  background: ${props => props.theme.background};
`;

const SectionCardPlain = styled.div`
  padding: 16px 0;
  background: transparent;
`;

const LeaseContextBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: ${props => props.theme.muted}20;
  border-radius: 10px;
  font-size: 13px;
  color: ${props => props.theme.mutedForeground};
  strong {
    color: ${props => props.theme.foreground};
  }
`;

const CollapsibleSection = styled.div`
  margin-top: 0;
`;

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${props => props.theme.muted}20;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: ${props => props.theme.muted}30;
  }
  @media (max-width: 767px) {
    padding: 8px 12px;
    border-radius: 8px;
  }
`;

const CollapsibleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
  @media (max-width: 767px) {
    font-size: 13px;
    gap: 6px;
  }
`;

const CollapsibleContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '5000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-top: ${props => props.$isExpanded ? '12px' : '0'};
  @media (max-width: 767px) {
    padding-top: ${props => props.$isExpanded ? '8px' : '0'};
  }
`;

const TipsBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  background: ${props => props.theme.muted}40;
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  margin-top: 8px;
  @media (max-width: 767px) {
    padding: 10px 12px;
    gap: 8px;
    border-radius: 8px;
    margin-top: 4px;
  }
`;

const TipsContent = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
  strong {
    color: ${props => props.theme.foreground};
  }
  ul {
    margin: 4px 0 0 0;
    padding-left: 18px;
  }
`;

const PlaceholderVar = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 5px;
  border-radius: 4px;
  font-family: ui-monospace, 'SF Mono', Monaco, monospace;
  font-size: 10px;
  font-weight: 500;
  background: ${props => props.theme.muted}50;
  color: ${props => props.theme.foreground};
  border: 1px solid ${props => props.theme.border};
  cursor: default;
`;

const TitleInputWrap = styled.div`
  input {
    border: 1px solid ${props => props.theme.border};
  }
  input:focus {
    border-color: ${props => props.theme.foreground};
    box-shadow: 0 0 0 2px ${props => props.theme.muted}40;
  }
`;

const PreviewScroll = styled.div`
  flex: 1;
  min-height: 280px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  background: ${props => props.theme.muted}15;
  @media (max-width: 767px) {
    min-height: 180px;
    padding: 10px 12px;
    border-radius: 8px;
  }
`;

const PreviewTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0 0 12px 0;
  @media (max-width: 767px) {
    font-size: 16px;
    margin: 0 0 8px 0;
  }
`;

const PreviewBody = styled.pre`
  font-size: 13px;
  color: ${props => props.theme.foreground};
  white-space: pre-wrap;
  font-family: inherit;
  line-height: 1.5;
  margin: 0;
  @media (max-width: 767px) {
    font-size: 12px;
  }
`;

const PreviewBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  @media (max-width: 767px) {
    margin-bottom: 6px;
    gap: 6px;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid ${props => props.theme.border};
  margin-top: 16px;
  @media (max-width: 767px) {
    padding-top: 10px;
    margin-top: 8px;
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

// Default template: one concise block each; minimal blank lines to reduce PDF pages. Placeholders filled from landlord (createdBy) and tenant when PDF is generated.
const DEFAULT_PARTIES = `PARTIES

I, {{landlord_name}}, of {{landlord_company}}, whose address is {{landlord_address}}, Phone {{landlord_phone}}, and Email {{landlord_email}}, hereby grant and enter into this Tenancy Agreement as the Landlord.

AND

I, {{tenant_name}}, holder of ID/Passport No. {{tenant_id}}, Phone {{tenant_phone}}, and Email {{tenant_email}}, hereby accept and agree to enter into this Tenancy Agreement as the Tenant, subject to the terms and conditions contained herein.

This Tenancy Agreement is made on {{agreement_date}}.`;

const DEFAULT_GENERAL = `GENERAL CONDITIONS
This agreement represents the full understanding between both parties. Any amendments must be mutually agreed upon in writing.`;

const DEFAULT_SIGNATURES = `DIGITAL SIGNATURES
Landlord: {{landlord_name}} – {{landlord_signature}} ({{landlord_signature_date}})
Tenant: {{tenant_name}} – {{tenant_signature}} ({{tenant_signature_date}})`;

const LEASE_DRAFT_KEY_PREFIX = 'lease-editor-draft-';

// Key terms reflect property only (partial payments, installments, late fees from property settings)
const DEFAULT_KEY_TERM_LABELS = [
  'Monthly rent',
  'Deposit',
  'Lease period',
  'Partial payment allowed',
  'Payment installments',
  'Late fees',
];

function buildKeyTermsSection(terms: { label: string; value: string }[]): string {
  const lines = terms
    .filter(t => t.label.trim())
    .map(t => `• ${t.label}: ${t.value.trim() || '—'}`);
  if (lines.length === 0) return '';
  return 'KEY TERMS\n' + lines.join('\n');
}

function buildFullContent(
  parties: string,
  general: string,
  keyTerms: { label: string; value: string }[],
  signatures: string
): string {
  const keySection = buildKeyTermsSection(keyTerms);
  const parts = [
    parties.trim(),
    general.trim(),
    keySection,
    signatures.trim(),
  ].filter(Boolean);
  return parts.join('\n\n');
}

export interface LeaseEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName?: string;
  leaseId?: string | null;
  initialTitle?: string;
  initialContent?: string;
  onSave?: (payload: { title: string; content: string }) => void | Promise<void>;
  onSendForSignature?: (leaseId: string) => void | Promise<void>;
}

const LeaseEditorModal: React.FC<LeaseEditorModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyName,
  leaseId,
  initialTitle = '',
  initialContent = '',
  onSave,
  onSendForSignature,
}) => {
  const toast = useToast();
  const [title, setTitle] = useState(initialTitle || 'RESIDENTIAL TENANCY AGREEMENT');
  const [parties, setParties] = useState(DEFAULT_PARTIES);
  const [general, setGeneral] = useState('');
  const [keyTerms, setKeyTerms] = useState<{ label: string; value: string }[]>(() =>
    DEFAULT_KEY_TERM_LABELS.map(label => ({ label, value: '' }))
  );
  const [signatures, setSignatures] = useState(DEFAULT_SIGNATURES);
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [partiesOpen, setPartiesOpen] = useState(false);
  const [keyTermsOpen, setKeyTermsOpen] = useState(false);
  const [signaturesOpen, setSignaturesOpen] = useState(false);
  const [placeholdersOpen, setPlaceholdersOpen] = useState(false);
  const [showLandlordSignatureConsent, setShowLandlordSignatureConsent] = useState(false);
  const [landlordSignatureConsentGiven, setLandlordSignatureConsentGiven] = useState(false);
  /** When true, general holds the full saved document from API; use it as-is to avoid duplicating PARTIES/KEY TERMS/SIGNATURES */
  const [contentSource, setContentSource] = useState<'api' | 'editor'>('editor');

  const generalInputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const { data: leaseData, isLoading: leaseLoading } = useQuery({
    queryKey: ['lease', leaseId] as const,
    queryFn: async () => {
      const res = await apiService.getLease(leaseId as string);
      return res.data;
    },
    enabled: isOpen && !!leaseId,
    refetchOnMount: 'always',
  });

  const { data: propertyDefaultData, isLoading: propertyDefaultLoading } = useQuery({
    queryKey: ['property-default-lease', propertyId] as const,
    queryFn: () => apiService.getPropertyDefaultLeaseContent(propertyId),
    enabled: isOpen && !!propertyId && !leaseId,
    refetchOnMount: 'always',
  });

  const saveLeaseMutation = useMutation({
    mutationFn: ({ leaseId: id, payload }: { leaseId: string; payload: { title: string; content: string } }) =>
      apiService.saveLeaseDocument(id, payload),
    onSuccess: (_, { leaseId: id }) => {
      queryClient.invalidateQueries({ queryKey: leaseQueryKey(id) });
    },
  });

  const sendForSignatureMutation = useMutation({
    mutationFn: (id: string) => apiService.sendLeaseForSignature(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: leaseQueryKey(id) });
    },
  });

  // Baseline for "already saved" lease: disable Save & Send until content is edited
  const savedBaselineRef = useRef<{ title: string; content: string } | null>(null);
  const restoredDraftForLeaseIdRef = useRef<string | null>(null);
  const prefilledKeyTermsForLeaseIdRef = useRef<string | null>(null);
  const latestDraftRef = useRef<{ title: string; parties: string; general: string; keyTerms: { label: string; value: string }[]; signatures: string }>({ title: '', parties: '', general: '', keyTerms: [], signatures: '' });
  const openLeaseIdRef = useRef<string | null>(null);
  const prevOpenRef = useRef(false);

  const isPropertyTemplateMode = isOpen && !!propertyId && !leaseId;
  useEffect(() => {
    if (!isOpen || !leaseId) {
      savedBaselineRef.current = null;
      restoredDraftForLeaseIdRef.current = null;
      prefilledKeyTermsForLeaseIdRef.current = null;
      if (!leaseId) setContentSource('editor');
    } else if (isOpen && !prevOpenRef.current) {
      restoredDraftForLeaseIdRef.current = null;
    }
    prevOpenRef.current = !!isOpen;
  }, [isOpen, leaseId]);

  // Keep latest draft ref in sync for flush-on-close
  useEffect(() => {
    latestDraftRef.current = { title, parties, general, keyTerms, signatures };
  }, [title, parties, general, keyTerms, signatures]);

  // Flush draft to localStorage when modal closes so we never lose input
  useEffect(() => {
    if (isOpen && leaseId) {
      openLeaseIdRef.current = leaseId;
    }
    return () => {
      const id = openLeaseIdRef.current;
      if (id) {
        try {
          localStorage.setItem(LEASE_DRAFT_KEY_PREFIX + id, JSON.stringify(latestDraftRef.current));
        } catch {
          /* ignore */
        }
        openLeaseIdRef.current = null;
      }
    };
  }, [isOpen, leaseId]);

  // Prefill key terms from lease + property when data loads (once per lease)
  useEffect(() => {
    if (!leaseId || !leaseData || prefilledKeyTermsForLeaseIdRef.current === leaseId) return;
    const data = leaseData as any;
    const startDate = data.startDate != null ? new Date(data.startDate) : undefined;
    const endDate = data.endDate != null ? new Date(data.endDate) : undefined;
    setKeyTerms(prev => {
      const next = [...prev];
      const rentIdx = next.findIndex(t => t.label.toLowerCase().includes('monthly rent'));
      if (rentIdx >= 0 && data.rentAmount != null) {
        const n = Number(data.rentAmount);
        next[rentIdx] = { ...next[rentIdx], value: `KES ${n.toLocaleString()}` };
      }
      const depIdx = next.findIndex(t => t.label.toLowerCase().includes('deposit') && !t.label.toLowerCase().includes('partial'));
      if (depIdx >= 0 && data.depositAmount != null) {
        const n = Number(data.depositAmount);
        next[depIdx] = { ...next[depIdx], value: `KES ${n.toLocaleString()}` };
      }
      const periodIdx = next.findIndex(t => t.label.toLowerCase().includes('lease period'));
      if (periodIdx >= 0 && startDate && endDate) {
        const start = startDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
        const end = endDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
        next[periodIdx] = { ...next[periodIdx], value: `${start} – ${end}` };
      }
      const partialIdx = next.findIndex(t => t.label.toLowerCase().includes('partial') && !t.label.toLowerCase().includes('installment'));
      if (partialIdx >= 0 && data.property?.allowPartialPayments !== undefined) {
        next[partialIdx] = {
          ...next[partialIdx],
          value: data.property.allowPartialPayments ? 'Yes' : 'No',
        };
      }
      const installIdx = next.findIndex(t => t.label.toLowerCase().includes('installment'));
      if (installIdx >= 0 && data.property) {
        const allow2 = data.property.allow2Installments !== false;
        const allow3 = data.property.allow3Installments !== false;
        const allow4 = data.property.allow4Installments !== false;
        const allowCustom = data.property.allowCustomPayments !== false;
        if (allow2 || allow3 || allow4 || allowCustom) {
          const parts: string[] = [];
          if (allow2) parts.push('2');
          if (allow3) parts.push('3');
          if (allow4) parts.push('4');
          if (allowCustom) parts.push('custom');
          next[installIdx] = { ...next[installIdx], value: `${parts.join(', ')} allowed` };
        }
      }
      const lateIdx = next.findIndex(t => t.label.toLowerCase().includes('late'));
      if (lateIdx >= 0 && data.property?.enableLateFees !== undefined) {
        next[lateIdx] = {
          ...next[lateIdx],
          value: data.property.enableLateFees ? 'Yes (per property policy)' : 'No',
        };
      }
      return next;
    });
    prefilledKeyTermsForLeaseIdRef.current = leaseId;
  }, [leaseId, leaseData]);

  // Restore saved lease content first (what the landlord pasted/saved); only use draft when there is no saved content.
  // When we have saved content, open in preview so the landlord sees their lease and can click Edit to change it.
  useEffect(() => {
    if (!isOpen || !leaseId || !leaseData) return;
    if (restoredDraftForLeaseIdRef.current === leaseId) return;
    const data = leaseData as { agreementContent?: string | null; title?: string | null };
    try {
      if (data.agreementContent?.trim()) {
        setGeneral(data.agreementContent.trim());
        if (data.title?.trim()) setTitle(data.title.trim());
        setContentSource('api'); // use general as full content to avoid duplicating PARTIES/KEY TERMS/SIGNATURES
        setIsPreview(true); // open in preview when content exists; user clicks Edit to change
      } else {
        setContentSource('editor');
        const raw = localStorage.getItem(LEASE_DRAFT_KEY_PREFIX + leaseId);
        if (raw) {
          const draft = JSON.parse(raw) as { title?: string; parties?: string; general?: string; keyTerms?: { label: string; value: string }[]; signatures?: string };
          if (draft.title !== undefined) setTitle(draft.title);
          if (draft.parties !== undefined) setParties(draft.parties);
          if (draft.general !== undefined) setGeneral(draft.general);
          if (Array.isArray(draft.keyTerms) && draft.keyTerms.length > 0) setKeyTerms(draft.keyTerms);
          if (draft.signatures !== undefined) setSignatures(draft.signatures);
          toast.success('Draft restored');
        }
      }
      restoredDraftForLeaseIdRef.current = leaseId;
    } catch {
      restoredDraftForLeaseIdRef.current = leaseId;
    }
  }, [isOpen, leaseId, leaseData]);

  // Property template mode (no tenants yet): restore property-wide default content
  useEffect(() => {
    if (!isPropertyTemplateMode || !propertyDefaultData) return;
    if (restoredDraftForLeaseIdRef.current === 'property-default') return;
    const content = propertyDefaultData.agreementContent?.trim();
    if (content) {
      setGeneral(content);
      setContentSource('api');
      setIsPreview(true);
    }
    restoredDraftForLeaseIdRef.current = 'property-default';
  }, [isPropertyTemplateMode, propertyDefaultData]);

  // Persist draft to localStorage (debounced) while editing so refresh doesn't clear input
  useEffect(() => {
    if (!leaseId || !isOpen) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(LEASE_DRAFT_KEY_PREFIX + leaseId, JSON.stringify(latestDraftRef.current));
      } catch {
        /* ignore quota / private */
      }
    }, 400);
    return () => clearTimeout(t);
  }, [leaseId, isOpen, title, parties, general, keyTerms, signatures]);

  // Reset / init from initialContent if provided (e.g. re-open with saved content we don't persist).
  // When leaseId is set, preview vs edit is set by the leaseData restore effect so we don't force edit here.
  useEffect(() => {
    if (isOpen) {
      if (initialTitle) setTitle(initialTitle);
      if (initialContent?.trim()) {
        setGeneral(initialContent.trim());
        setContentSource('api');
        setIsPreview(true);
      } else if (!leaseId) {
        setContentSource('editor');
        setIsPreview(false);
      }
    }
  }, [isOpen, initialTitle, initialContent, leaseId]);

  const addKeyTermRow = () => {
    setKeyTerms(prev => [...prev, { label: '', value: '' }]);
  };

  const updateKeyTerm = (index: number, field: 'label' | 'value', value: string) => {
    setKeyTerms(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeKeyTerm = (index: number) => {
    setKeyTerms(prev => prev.filter((_, i) => i !== index));
  };

  const resetParties = () => setParties(DEFAULT_PARTIES);
  const resetGeneral = () => setGeneral(DEFAULT_GENERAL);
  const resetKeyTerms = () =>
    setKeyTerms(DEFAULT_KEY_TERM_LABELS.map(label => ({ label, value: '' })));
  const resetSignatures = () => setSignatures(DEFAULT_SIGNATURES);

  const handlePasteGeneral = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setGeneral(text);
      toast.success('Lease content pasted from clipboard');
    } catch {
      toast.error('Failed to paste from clipboard');
    }
  };

  const fullContent = contentSource === 'api' ? general : buildFullContent(parties, general, keyTerms, signatures);

  // When lease is already saved, capture baseline content so we can disable Save & Send until user edits
  useEffect(() => {
    if (!isOpen) return;
    if (leaseData?.currentDocumentUrl && leaseId && savedBaselineRef.current === null) {
      savedBaselineRef.current = { title, content: fullContent };
    }
  }, [isOpen, leaseId, leaseData?.currentDocumentUrl, title, fullContent]);

  const isAlreadySaved = !!leaseData?.currentDocumentUrl;
  const hasUnsavedEdits = !savedBaselineRef.current ? true : (title !== savedBaselineRef.current.title || fullContent !== savedBaselineRef.current.content);

  const handleSaveTemplate = async () => {
    if (!propertyId || !title.trim() || !fullContent.trim()) {
      toast.error('Please add a title and content');
      return;
    }
    setIsSaving(true);
    try {
      await apiService.savePropertyDefaultLeaseContent(propertyId, fullContent);
      queryClient.invalidateQueries({ queryKey: ['property-default-lease', propertyId] });
      toast.success('Property lease template saved. New tenants will get this to sign.');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSendClick = () => {
    if (!title.trim() || !fullContent.trim()) {
      toast.error('Please add a title and content');
      return;
    }
    if (!leaseId) return;
    setShowLandlordSignatureConsent(true);
  };

  const handleConsentConfirm = async () => {
    if (!landlordSignatureConsentGiven) return;
    setShowLandlordSignatureConsent(false);
    if (!title.trim() || !fullContent.trim() || !leaseId) return;
    const doSave = onSave ?? ((payload: { title: string; content: string }) => saveLeaseMutation.mutateAsync({ leaseId, payload }));
    const doSend = onSendForSignature ?? ((id: string) => sendForSignatureMutation.mutateAsync(id));
    setIsSaving(true);
    try {
      await doSave({ title: title.trim(), content: fullContent });
      savedBaselineRef.current = { title: title.trim(), content: fullContent };
      try {
        localStorage.removeItem(LEASE_DRAFT_KEY_PREFIX + leaseId);
      } catch {
        /* ignore */
      }
      toast.success('Lease saved.');
      await doSend(leaseId);
      toast.success('Lease sent for signature. Tenant can sign from their portal.');
      onClose();
    } catch {
      toast.error('Save or send failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const wordCount = fullContent.trim() ? fullContent.trim().split(/\s+/).filter(Boolean).length : 0;

  const handleDownload = async () => {
    if (!leaseId || !leaseData?.currentDocumentUrl) return;
    setIsDownloading(true);
    try {
      const blob = await apiService.getLeaseDocumentDownload(leaseId, true);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lease-${(title || 'agreement').replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Lease PDF downloaded');
    } catch {
      toast.error('Failed to download lease PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Lease Agreement Editor"
      size="lg"
      zIndex={1050}
    >
      <ModalMainContent>
        {isOpen && leaseId && leaseLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '48px 24px', minHeight: 200 }}>
            <Loader2 size={32} style={{ color: 'var(--primary)', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 14, color: 'var(--mutedForeground)' }}>Loading lease...</span>
          </div>
        ) : isOpen && propertyId && !leaseId && propertyDefaultLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '48px 24px', minHeight: 200 }}>
            <Loader2 size={32} style={{ color: 'var(--primary)', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 14, color: 'var(--mutedForeground)' }}>Loading lease...</span>
          </div>
        ) : (
        <>
        <BodyTopRow>
          <DescriptionText>
            {propertyName
              ? `Edit the lease for ${propertyName}. Parties and signatures use placeholders (e.g. {{tenant_name}}) filled when the tenant views or signs.`
              : 'Edit your tenancy agreement. Paste your terms in General conditions; add key terms (utilities, late fees, etc.).'}
          </DescriptionText>
          <div className="body-top-right">
            {leaseData?.currentDocumentUrl && (
              <Badge variant="secondary" style={{ fontSize: 11 }}>
                Document saved
              </Badge>
            )}
            <Badge variant="outline" style={{ fontSize: 12 }}>
              {wordCount} words
            </Badge>
            <Button
              variant={isPreview ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setIsPreview(!isPreview)}
            >
              <Eye size={14} style={{ marginRight: 6 }} />
              {isPreview ? 'Edit' : 'Preview'}
            </Button>
          </div>
        </BodyTopRow>

        <ContentArea>
          {isPreview ? (
            <>
              <PreviewBadgeRow>
                <Sparkles size={16} style={{ color: 'var(--primary)' }} />
                <span style={{ fontSize: 14, fontWeight: 500 }}>Tenant preview</span>
                <Badge variant="secondary" style={{ fontSize: 11 }}>How it appears in download</Badge>
              </PreviewBadgeRow>
              <PreviewScroll>
                <PreviewTitle>{title || 'Residential Tenancy Agreement'}</PreviewTitle>
                <PreviewBody>{fullContent}</PreviewBody>
              </PreviewScroll>
            </>
          ) : (
            <>
              {leaseId && (
                <LeaseContextBar>
                  {leaseLoading ? (
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                  ) : leaseData?.property ? (
                    <>
                      <span><strong>Property:</strong> {leaseData.property.name}</span>
                      <span style={{ color: 'var(--mutedForeground)', fontSize: 13 }}>
                        Property-wide agreement — applies to all tenants at this property
                      </span>
                    </>
                  ) : null}
                </LeaseContextBar>
              )}

              <SectionCardPlain>
                <LabelRow htmlFor="lease-title">Agreement title</LabelRow>
                <TitleInputWrap>
                  <ModalInput
                    id="lease-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. RESIDENTIAL TENANCY AGREEMENT"
                  />
                </TitleInputWrap>
              </SectionCardPlain>

              <CollapsibleSection>
                <CollapsibleHeader onClick={() => setPartiesOpen(!partiesOpen)}>
                  <CollapsibleTitle>
                    {partiesOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    1. Parties (top)
                  </CollapsibleTitle>
                </CollapsibleHeader>
                <CollapsibleContent $isExpanded={partiesOpen}>
                  <LabelRow htmlFor="lease-parties">
                    {'Landlord and tenant details – placeholders (e.g. {{landlord_name}}, {{tenant_name}}) are shown as chips and cannot be deleted.'}
                  </LabelRow>
                  <PlaceholderEditor
                    id="lease-parties"
                    value={parties}
                    onChange={setParties}
                    placeholder={DEFAULT_PARTIES}
                    minHeight={180}
                    aria-label="Parties section with placeholders"
                  />
                </CollapsibleContent>
              </CollapsibleSection>

              <SectionCardPlain>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <SectionHeading style={{ margin: 0 }}>2. General conditions</SectionHeading>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => generalInputRef.current?.focus()}
                      style={{ gap: 6, height: 28, fontSize: 12 }}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDownload}
                      disabled={!leaseData?.currentDocumentUrl || isDownloading}
                      title={!leaseData?.currentDocumentUrl ? 'Save the lease first to download PDF' : undefined}
                      style={{ gap: 6, height: 28, fontSize: 12 }}
                    >
                      {isDownloading ? (
                        <Loader2 size={14} className="spinner" />
                      ) : (
                        <Download size={14} />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </div>
                <GeneralConditionsLabelRow>
                  <LabelRow htmlFor="lease-general" style={{ marginBottom: 0, fontWeight: 500 }}>
                    Lease Content
                  </LabelRow>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handlePasteGeneral}
                    style={{ gap: 6, height: 28, fontSize: 12 }}
                  >
                    <Copy size={14} />
                    Paste from Clipboard
                  </Button>
                </GeneralConditionsLabelRow>
                <GeneralConditionsInput
                  ref={generalInputRef}
                  id="lease-general"
                  value={general}
                  onChange={(e) => setGeneral(e.target.value)}
                  placeholder={`Paste your full lease agreement here...

RESIDENTIAL LEASE AGREEMENT

This Lease Agreement is entered into as of [DATE], by and between:

LANDLORD: [Landlord Name]
TENANT: [Tenant Name]

PROPERTY ADDRESS: [Full Address]

1. TERM OF LEASE
The lease term shall begin on [Start Date] and end on [End Date].

2. RENT
Monthly rent shall be $[Amount], due on the [Day] of each month...

[Continue with full lease terms...]`}
                />
              </SectionCardPlain>

              <CollapsibleSection>
                <CollapsibleHeader onClick={() => setKeyTermsOpen(!keyTermsOpen)}>
                  <CollapsibleTitle>
                    {keyTermsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    3. Key terms (list)
                  </CollapsibleTitle>
                </CollapsibleHeader>
                <CollapsibleContent $isExpanded={keyTermsOpen}>
                  <LabelRow>Utilities, late fees, partial payment, notice period, pets. Filled from property when available; edit as needed.</LabelRow>
                  <KeyTermsList>
                    {keyTerms.map((term, index) => (
                      <KeyTermRow key={index}>
                        <span className="term-label" style={{ width: '100%', minWidth: 120 }}>
                          <ModalInput
                            value={term.label}
                            onChange={(e) => updateKeyTerm(index, 'label', e.target.value)}
                            placeholder="e.g. Utilities"
                          />
                        </span>
                        <span className="term-input">
                          <ModalInput
                            value={term.value}
                            onChange={(e) => updateKeyTerm(index, 'value', e.target.value)}
                            placeholder="Value"
                          />
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeKeyTerm(index)}
                          title="Remove row"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </KeyTermRow>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addKeyTermRow}>
                      <Plus size={14} style={{ marginRight: 6 }} />
                      Add row
                    </Button>
                  </KeyTermsList>
                </CollapsibleContent>
              </CollapsibleSection>

              <CollapsibleSection>
                <CollapsibleHeader onClick={() => setSignaturesOpen(!signaturesOpen)}>
                  <CollapsibleTitle>
                    {signaturesOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    4. Signatures (bottom)
                  </CollapsibleTitle>
                </CollapsibleHeader>
                <CollapsibleContent $isExpanded={signaturesOpen}>
                  <LabelRow htmlFor="lease-signatures">
                    {'This block appears at the end. Placeholders {{landlord_name}} and {{tenant_name}} are chips and are filled when the tenant signs.'}
                  </LabelRow>
                  <PlaceholderEditor
                    id="lease-signatures"
                    value={signatures}
                    onChange={setSignatures}
                    placeholder={DEFAULT_SIGNATURES}
                    minHeight={120}
                    aria-label="Signatures section with placeholders"
                  />
                </CollapsibleContent>
              </CollapsibleSection>

              <CollapsibleSection>
                <CollapsibleHeader onClick={() => setPlaceholdersOpen(!placeholdersOpen)}>
                  <CollapsibleTitle>
                    {placeholdersOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    Placeholders (filled when tenant views or downloads)
                  </CollapsibleTitle>
                </CollapsibleHeader>
                <CollapsibleContent $isExpanded={placeholdersOpen}>
                  <TipsBox>
                    <AlertCircle size={18} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
                    <TipsContent>
                      <ul>
                        <li><PlaceholderVar>{'{{agreement_date}}'}</PlaceholderVar> – date of agreement</li>
                        <li>Landlord: <PlaceholderVar>{'{{landlord_name}}'}</PlaceholderVar>, <PlaceholderVar>{'{{landlord_company}}'}</PlaceholderVar>, <PlaceholderVar>{'{{landlord_id}}'}</PlaceholderVar>, <PlaceholderVar>{'{{landlord_address}}'}</PlaceholderVar>, <PlaceholderVar>{'{{landlord_phone}}'}</PlaceholderVar>, <PlaceholderVar>{'{{landlord_email}}'}</PlaceholderVar></li>
                        <li><PlaceholderVar>{'{{tenant_name}}'}</PlaceholderVar>, <PlaceholderVar>{'{{tenant_id}}'}</PlaceholderVar>, <PlaceholderVar>{'{{tenant_phone}}'}</PlaceholderVar>, <PlaceholderVar>{'{{tenant_email}}'}</PlaceholderVar></li>
                        <li>Signatures: <PlaceholderVar>{'{{landlord_signature}}'}</PlaceholderVar>, <PlaceholderVar>{'{{landlord_signature_date}}'}</PlaceholderVar>, <PlaceholderVar>{'{{tenant_signature}}'}</PlaceholderVar>, <PlaceholderVar>{'{{tenant_signature_date}}'}</PlaceholderVar> – filled when the document is signed</li>
                      </ul>
                    </TipsContent>
                  </TipsBox>
                </CollapsibleContent>
              </CollapsibleSection>
            </>
          )}
        </ContentArea>

        <FooterRow>
          <FooterRight>
            {leaseId && (
              <Button
                onClick={handleSaveAndSendClick}
                disabled={isSaving || isSending || !title.trim() || !fullContent.trim() || (isAlreadySaved && !hasUnsavedEdits)}
                title={isAlreadySaved && !hasUnsavedEdits ? 'Edit the lease content to enable Save & send' : undefined}
              >
                {isSaving || isSending ? (
                  <>
                    <span style={{ marginRight: 8 }} className="spinner" />
                    Saving & sending...
                  </>
                ) : (
                  <>
                    <Send size={16} style={{ marginRight: 8 }} />
                    Save & send for signature
                  </>
                )}
              </Button>
            )}
          </FooterRight>
        </FooterRow>
        </>
        )}
      </ModalMainContent>
    </Modal>

    {showLandlordSignatureConsent && (
      <Modal
        isOpen={true}
        onClose={() => setShowLandlordSignatureConsent(false)}
        title="Your digital signature"
        size="sm"
        zIndex={1060}
      >
        <ModalMainContent>
          <p style={{ fontSize: 14, color: 'var(--mutedForeground)', margin: '0 0 12px 0', lineHeight: 1.5 }}>
            When you <strong>save</strong> this lease, the system will record your <strong>digital signature</strong> for this lease. It is used to attest that you approved this document and to verify the lease record.
          </p>
          <label
            htmlFor="landlord-signature-consent"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '12px 0',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <Checkbox
              id="landlord-signature-consent"
              checked={landlordSignatureConsentGiven}
              onChange={setLandlordSignatureConsentGiven}
              size="lg"
            />
            <span>I consent to the use of a system-generated digital signature for this lease.</span>
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8 }}>
            <Button
              onClick={handleConsentConfirm}
              disabled={!landlordSignatureConsentGiven || isSaving}
            >
              {isSaving ? (
                <>
                  <span style={{ marginRight: 8 }} className="spinner" />
                  Saving & sending...
                </>
              ) : (
                <>
                  <Send size={16} style={{ marginRight: 8 }} />
                  I consent and save & send
                </>
              )}
            </Button>
          </div>
        </ModalMainContent>
      </Modal>
    )}
  </>
  );
};

export default LeaseEditorModal;
